import { useCallback } from 'react';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';
import { decodeHtmlEntities } from '@/lib/mappers/templates';

export interface GetEmailsParams
  extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  sourceType?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  templateTypeId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface Email {
  id: number;
  customerId: number;
  customerName: string;
  customerAccountNumber: string;
  sourceType: string;
  sourceId: number | null;
  toRecipients: string;
  ccRecipients: string | null;
  subject: string;
  templateName: string | null;
  templateTypeId: number | null;
  status: string;
  errorMessage: string | null;
  sentAt: string | null;
  sentByName: string;
  sentByEmail: string | null;
  messageId: string | null;
  createdAt: string;
  emailChannel: string;
}

export interface EmailDetail extends Email {
  htmlBody: string | null;
  templateId: number | null;
  isSubjectModified: boolean;
  isBodyModified: boolean;
  attachOutstandingInvoices: boolean;
  retryCount: number;
  updatedAt: string;
}

interface EmailsApiResponse {
  data: Email[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface EmailsListResult {
  emails: Email[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

// ── Mock data ──────────────────────────────────────────────────────────────────

const MOCK_EMAILS: Email[] = [
  {
    id: 1,
    customerId: 101,
    customerName: 'Acme Corp',
    customerAccountNumber: 'ACC-001',
    sourceType: 'Collection',
    sourceId: 10,
    toRecipients: 'john@acme.com',
    ccRecipients: null,
    subject: 'Outstanding Invoice Reminder - INV-2024-001',
    templateName: 'Collection Reminder',
    templateTypeId: 1,
    status: 'Delivered',
    errorMessage: null,
    sentAt: '2025-12-15T10:30:00',
    sentByName: 'Jane Smith',
    sentByEmail: 'jane@company.com',
    messageId: 'msg-001',
    createdAt: '2025-12-15T10:29:00',
    emailChannel: 'Queued',
  },
  {
    id: 2,
    customerId: 102,
    customerName: 'Global Industries',
    customerAccountNumber: 'ACC-002',
    sourceType: 'Promise',
    sourceId: 20,
    toRecipients: 'finance@global.com',
    ccRecipients: 'manager@global.com',
    subject: 'Promise to Pay Confirmation',
    templateName: 'Promise Confirmation',
    templateTypeId: 1,
    status: 'Delivered',
    errorMessage: null,
    sentAt: '2025-12-14T14:00:00',
    sentByName: 'System',
    sentByEmail: null,
    messageId: 'msg-002',
    createdAt: '2025-12-14T13:59:00',
    emailChannel: 'Queued',
  },
  {
    id: 3,
    customerId: 103,
    customerName: 'Tech Solutions Ltd',
    customerAccountNumber: 'ACC-003',
    sourceType: 'Dispute',
    sourceId: 30,
    toRecipients: 'billing@techsol.com',
    ccRecipients: null,
    subject: 'Dispute Resolution Update - DSP-045',
    templateName: 'Dispute Update',
    templateTypeId: 1,
    status: 'Failed',
    errorMessage: 'SMTP connection timeout',
    sentAt: null,
    sentByName: 'Mike Johnson',
    sentByEmail: 'mike@company.com',
    messageId: null,
    createdAt: '2025-12-13T09:15:00',
    emailChannel: 'Queued',
  },
  {
    id: 4,
    customerId: 104,
    customerName: 'Northern Supplies',
    customerAccountNumber: 'ACC-004',
    sourceType: 'Customer',
    sourceId: 104,
    toRecipients: 'accounts@northern.com',
    ccRecipients: null,
    subject: 'Statement of Account - December 2025',
    templateName: 'Monthly Statement',
    templateTypeId: 2,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'System',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-12-12T07:59:00',
    emailChannel: 'Queued',
  },
  {
    id: 5,
    customerId: 105,
    customerName: 'Bright Electronics',
    customerAccountNumber: 'ACC-005',
    sourceType: 'Collection',
    sourceId: 11,
    toRecipients: 'pay@bright.com',
    ccRecipients: 'cfo@bright.com',
    subject: 'Final Payment Notice - Past Due 60 Days',
    templateName: 'Final Notice',
    templateTypeId: 1,
    status: 'Delivered',
    errorMessage: null,
    sentAt: '2025-12-11T16:45:00',
    sentByName: 'Sarah Lee',
    sentByEmail: 'sarah@company.com',
    messageId: 'msg-005',
    createdAt: '2025-12-11T16:44:00',
    emailChannel: 'Queued',
  },
  {
    id: 6,
    customerId: 106,
    customerName: 'Metro Services',
    customerAccountNumber: 'ACC-006',
    sourceType: 'Promise',
    sourceId: 21,
    toRecipients: 'ap@metro.com',
    ccRecipients: null,
    subject: 'Promise to Pay Reminder - Due Tomorrow',
    templateName: 'Promise Reminder',
    templateTypeId: 1,
    status: 'Pending',
    errorMessage: null,
    sentAt: null,
    sentByName: 'System',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-12-16T06:00:00',
    emailChannel: 'Queued',
  },
  {
    id: 7,
    customerId: 107,
    customerName: 'Coastal Trading Co',
    customerAccountNumber: 'ACC-007',
    sourceType: 'Customer',
    sourceId: 107,
    toRecipients: 'info@coastal.com',
    ccRecipients: null,
    subject: 'Welcome to Credit Control Portal',
    templateName: 'Welcome Email',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'Admin Portal',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-12-10T11:19:00',
    emailChannel: 'Instant',
  },
  {
    id: 8,
    customerId: 108,
    customerName: 'Summit Partners',
    customerAccountNumber: 'ACC-008',
    sourceType: 'Dispute',
    sourceId: 31,
    toRecipients: 'disputes@summit.com',
    ccRecipients: 'legal@summit.com',
    subject: 'Dispute Acknowledgement - DSP-078',
    templateName: 'Dispute Acknowledgement',
    templateTypeId: 1,
    status: 'Delivered',
    errorMessage: null,
    sentAt: '2025-12-09T13:30:00',
    sentByName: 'Jane Smith',
    sentByEmail: 'jane@company.com',
    messageId: 'msg-008',
    createdAt: '2025-12-09T13:29:00',
    emailChannel: 'Queued',
  },
  {
    id: 9,
    customerId: 101,
    customerName: 'Acme Corp',
    customerAccountNumber: 'ACC-001',
    sourceType: 'Collection',
    sourceId: 12,
    toRecipients: 'john@acme.com',
    ccRecipients: null,
    subject: 'Payment Received - Thank You',
    templateName: 'Payment Thanks',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: '2025-12-08T09:00:00',
    sentByName: 'System',
    sentByEmail: null,
    messageId: 'msg-009',
    createdAt: '2025-12-08T08:59:00',
    emailChannel: 'Queued',
  },
  {
    id: 10,
    customerId: 109,
    customerName: 'Valley Manufacturing',
    customerAccountNumber: 'ACC-009',
    sourceType: 'Customer',
    sourceId: 109,
    toRecipients: 'finance@valley.com',
    ccRecipients: null,
    subject: 'Credit Limit Review Notice',
    templateName: null,
    templateTypeId: null,
    status: 'Failed',
    errorMessage: 'Invalid recipient address',
    sentAt: null,
    sentByName: 'Mike Johnson',
    sentByEmail: 'mike@company.com',
    messageId: null,
    createdAt: '2025-12-07T15:10:00',
    emailChannel: 'Queued',
  },
  {
    id: 11,
    customerId: 110,
    customerName: 'Pacific Traders',
    customerAccountNumber: 'ACC-010',
    sourceType: 'Collection',
    sourceId: 13,
    toRecipients: 'accounts@pacific.com',
    ccRecipients: null,
    subject: 'Overdue Invoice Reminder - 30 Days',
    templateName: '30 Day Reminder',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'Sarah Lee',
    sentByEmail: 'sarah@company.com',
    messageId: null,
    createdAt: '2025-12-06T09:59:00',
    emailChannel: 'Instant',
  },
  {
    id: 12,
    customerId: 111,
    customerName: 'Diamond Logistics',
    customerAccountNumber: 'ACC-011',
    sourceType: 'Promise',
    sourceId: 22,
    toRecipients: 'pay@diamond.com',
    ccRecipients: null,
    subject: 'Promise Broken - Escalation Notice',
    templateName: 'Promise Broken',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: '2025-12-05T14:30:00',
    sentByName: 'System',
    sentByEmail: null,
    messageId: 'msg-012',
    createdAt: '2025-12-05T14:29:00',
    emailChannel: 'Queued',
  },
  {
    id: 13,
    customerId: 112,
    customerName: 'Evergreen Retail',
    customerAccountNumber: 'ACC-012',
    sourceType: 'Dispute',
    sourceId: 32,
    toRecipients: 'billing@evergreen.com',
    ccRecipients: null,
    subject: 'Dispute Resolved - Credit Note Issued',
    templateName: 'Dispute Resolved',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: '2025-12-04T11:00:00',
    sentByName: 'Jane Smith',
    sentByEmail: 'jane@company.com',
    messageId: 'msg-013',
    createdAt: '2025-12-04T10:59:00',
    emailChannel: 'Queued',
  },
  {
    id: 14,
    customerId: 113,
    customerName: 'Sunrise Foods',
    customerAccountNumber: 'ACC-013',
    sourceType: 'Customer',
    sourceId: 113,
    toRecipients: 'accounts@sunrise.com',
    ccRecipients: 'manager@sunrise.com',
    subject: 'Account On Stop Notification',
    templateName: 'On Stop Notice',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'Mike Johnson',
    sentByEmail: 'mike@company.com',
    messageId: null,
    createdAt: '2025-12-03T09:44:00',
    emailChannel: 'Instant',
  },
  {
    id: 15,
    customerId: 114,
    customerName: 'Atlas Construction',
    customerAccountNumber: 'ACC-014',
    sourceType: 'Collection',
    sourceId: 14,
    toRecipients: 'finance@atlas.com',
    ccRecipients: null,
    subject: 'Pre-Legal Action Warning',
    templateName: 'Pre-Legal Warning',
    templateTypeId: 1,
    status: 'Pending',
    errorMessage: null,
    sentAt: null,
    sentByName: 'System',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-12-16T07:00:00',
    emailChannel: 'Queued',
  },
  {
    id: 16,
    customerId: 115,
    customerName: 'Harbor Shipping',
    customerAccountNumber: 'ACC-015',
    sourceType: 'Promise',
    sourceId: 23,
    toRecipients: 'ap@harbor.com',
    ccRecipients: null,
    subject: 'Promise to Pay Created - PTP-156',
    templateName: 'Promise Created',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: '2025-12-02T16:00:00',
    sentByName: 'Sarah Lee',
    sentByEmail: 'sarah@company.com',
    messageId: 'msg-016',
    createdAt: '2025-12-02T15:59:00',
    emailChannel: 'Queued',
  },
  {
    id: 17,
    customerId: 116,
    customerName: 'Pinnacle Group',
    customerAccountNumber: 'ACC-016',
    sourceType: 'Dispute',
    sourceId: 33,
    toRecipients: 'disputes@pinnacle.com',
    ccRecipients: null,
    subject: 'Dispute Escalated to Manager',
    templateName: 'Dispute Escalation',
    templateTypeId: 1,
    status: 'Failed',
    errorMessage: 'Mailbox full',
    sentAt: null,
    sentByName: 'Jane Smith',
    sentByEmail: 'jane@company.com',
    messageId: null,
    createdAt: '2025-12-01T12:30:00',
    emailChannel: 'Queued',
  },
  {
    id: 18,
    customerId: 117,
    customerName: 'Redwood Timber',
    customerAccountNumber: 'ACC-017',
    sourceType: 'Customer',
    sourceId: 117,
    toRecipients: 'billing@redwood.com',
    ccRecipients: null,
    subject: 'Monthly Ageing Report Attached',
    templateName: 'Ageing Report',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'System',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-11-30T07:59:00',
    emailChannel: 'Instant',
  },
  {
    id: 19,
    customerId: 118,
    customerName: 'Blue Ocean Ltd',
    customerAccountNumber: 'ACC-018',
    sourceType: 'Collection',
    sourceId: 15,
    toRecipients: 'pay@blueocean.com',
    ccRecipients: 'cfo@blueocean.com',
    subject: 'Urgent: Account Past Due 90+ Days',
    templateName: '90 Day Urgent',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: '2025-11-29T11:15:00',
    sentByName: 'Mike Johnson',
    sentByEmail: 'mike@company.com',
    messageId: 'msg-019',
    createdAt: '2025-11-29T11:14:00',
    emailChannel: 'Queued',
  },
  {
    id: 20,
    customerId: 119,
    customerName: 'Crown Enterprises',
    customerAccountNumber: 'ACC-019',
    sourceType: 'Promise',
    sourceId: 24,
    toRecipients: 'finance@crown.com',
    ccRecipients: null,
    subject: 'Promise Fulfilled - Thank You',
    templateName: 'Promise Fulfilled',
    templateTypeId: 1,
    status: 'Accepted',
    errorMessage: null,
    sentAt: null,
    sentByName: 'System',
    sentByEmail: null,
    messageId: null,
    createdAt: '2025-11-28T13:59:00',
    emailChannel: 'Queued',
  },
];

const MOCK_EMAIL_DETAILS: Record<number, EmailDetail> = Object.fromEntries(
  MOCK_EMAILS.map((ej) => [
    ej.id,
    {
      ...ej,
      htmlBody: `<html><body><h2>${ej.subject}</h2><p>Dear ${ej.customerName},</p><p>This is a sample email body for the email job.</p><p>Thank you for your attention to this matter.</p><br/><p>Best regards,<br/>${ej.sentByName}</p></body></html>`,
      templateId: ej.templateName ? ej.id * 10 : null,
      isSubjectModified: false,
      isBodyModified: false,
      attachOutstandingInvoices: ej.sourceType === 'Collection',
      retryCount: ej.status === 'Failed' ? 3 : 0,
      updatedAt: ej.createdAt,
    },
  ]),
);

// ── Mock helpers ───────────────────────────────────────────────────────────────

function filterMockEmails(jobs: Email[], params: GetEmailsParams): Email[] {
  let filtered = [...jobs];
  if (params.search) {
    const s = params.search.toLowerCase();
    filtered = filtered.filter(
      (x) =>
        x.subject.toLowerCase().includes(s) ||
        x.toRecipients.toLowerCase().includes(s) ||
        x.customerName.toLowerCase().includes(s) ||
        x.sentByName.toLowerCase().includes(s),
    );
  }
  if (params.status) {
    const statuses = params.status.split(',');
    filtered = filtered.filter((x) => statuses.includes(x.status));
  }
  if (params.sourceType) {
    const types = params.sourceType.split(',');
    filtered = filtered.filter((x) => types.includes(x.sourceType));
  }
  if (params.templateTypeId) {
    if (params.templateTypeId === '__none__') {
      filtered = filtered.filter((x) => x.templateTypeId === null);
    } else {
      const ids = params.templateTypeId.split(',').map(Number);
      filtered = filtered.filter(
        (x) => x.templateTypeId !== null && ids.includes(x.templateTypeId),
      );
    }
  }
  if (params.dateFrom) {
    const from = new Date(params.dateFrom);
    filtered = filtered.filter((x) => new Date(x.createdAt) >= from);
  }
  if (params.dateTo) {
    const to = new Date(params.dateTo);
    to.setDate(to.getDate() + 1);
    filtered = filtered.filter((x) => new Date(x.createdAt) < to);
  }
  return filtered;
}

function getEmailField(
  job: Email,
  key: string,
): string | number | null | undefined {
  switch (key) {
    case 'subject':
      return job.subject;
    case 'customerName':
      return job.customerName;
    case 'toRecipients':
      return job.toRecipients;
    case 'ccRecipients':
      return job.ccRecipients;
    case 'sourceType':
      return job.sourceType;
    case 'templateName':
      return job.templateName;
    case 'templateTypeId':
      return job.templateTypeId;
    case 'status':
      return job.status;
    case 'sentByName':
      return job.sentByName;
    case 'sentAt':
      return job.sentAt;
    case 'createdAt':
      return job.createdAt;
    case 'errorMessage':
      return job.errorMessage;
    default:
      return job.createdAt;
  }
}

function sortMockEmails(
  jobs: Email[],
  sortBy?: string,
  sortOrder?: string,
): Email[] {
  const sorted = [...jobs];
  const dir = sortOrder === 'asc' ? 1 : -1;
  const key = sortBy ?? 'createdAt';
  sorted.sort((a, b) => {
    const aVal = getEmailField(a, key);
    const bVal = getEmailField(b, key);
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string' && typeof bVal === 'string')
      return aVal.localeCompare(bVal) * dir;
    return 0;
  });
  return sorted;
}

function computeMockPagination(
  totalItems: number,
  page: number,
  pageSize: number,
) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useEmailsList(params?: GetEmailsParams) {
  const { apiGet } = useApi();
  const queryParams: GetEmailsParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ...params,
  };

  const filteredMock = filterMockEmails(MOCK_EMAILS, queryParams);
  const sortedMock = sortMockEmails(
    filteredMock,
    queryParams.sortBy,
    queryParams.sortOrder,
  );
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const mockPaged = sortedMock.slice(
    (mockPage - 1) * mockPageSize,
    mockPage * mockPageSize,
  );
  const mockResult: EmailsListResult = {
    emails: mockPaged,
    pagination: computeMockPagination(
      sortedMock.length,
      mockPage,
      mockPageSize,
    ),
  };

  return useModeAwareQuery<EmailsListResult, Error>(
    ['emails', queryParams],
    async ({ signal }) => {
      const flatParams: Record<string, string | number | boolean> = {};
      for (const [key, value] of Object.entries(queryParams)) {
        if (value === undefined) continue;
        const paramKey = key === 'search' ? 'summary' : key;
        flatParams[paramKey] = value;
      }
      const response = await apiGet<EmailsApiResponse>(
        '/api/v1/emails',
        flatParams,
        signal,
      );
      return {
        emails: response.data.map((e) => ({
          ...e,
          subject: decodeHtmlEntities(e.subject ?? ''),
        })),
        pagination: response.pagination,
      };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export interface SaveEmailHistoryRequest {
  customerId: number;
  sourceType: string;
  toRecipients: string;
  subject: string;
  htmlBody: string;
  sourceId?: number | null;
  ccRecipients?: string | null;
  templateId?: number | null;
  templateName?: string | null;
  isSubjectModified?: boolean;
  isBodyModified?: boolean;
  attachOutstandingInvoices?: boolean;
}

export function useSaveEmailHistory() {
  const { apiPost } = useApi();

  const saveEmailHistory = useCallback(
    async (request: SaveEmailHistoryRequest) => {
      return await apiPost<{ id: number }>('/api/v1/emails/history', request);
    },
    [apiPost],
  );

  return { saveEmailHistory };
}

export function useEmailDetail(
  emailId: number | null,
  emailChannel: string | null,
) {
  const { apiGet } = useApi();

  const mockDetail = emailId ? (MOCK_EMAIL_DETAILS[emailId] ?? null) : null;

  return useModeAwareQuery<EmailDetail | null, Error>(
    ['emailDetail', emailId, emailChannel],
    async ({ signal }) => {
      if (!emailId || !emailChannel) return null;
      const detail = await apiGet<EmailDetail>(
        `/api/v1/emails/${emailId}`,
        { emailChannel },
        signal,
      );
      return { ...detail, subject: decodeHtmlEntities(detail.subject ?? '') };
    },
    mockDetail,
    {
      enabled: emailId !== null && emailChannel !== null,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
